import React, { useEffect, useState, useCallback } from 'react';
import {
	Box,
	Chip,
	Switch,
	TextField,
	Toolbar,
	Typography,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { differenceInMilliseconds, format } from 'date-fns';
import humanizeDuration from 'humanize-duration';
import { Delete } from '@mui/icons-material';
import { RegisteredPlates } from '../../../CANpark/types/registeredPlates';
import { getRegisteredPlatesByParkingLocation } from 'api/canpark/registeredPlates/getRegisteredPlatesByParkingLocation';
import { deleteRegisteredPlate } from 'api/canpark/registeredPlates/deleteRegisteredPlate';
import debounce from 'lodash/debounce';
import { normalizeLicensePlate } from '../../../CANpark/utils/normalizeLicensePlate';
import { stateProvinceList } from '../../../CANpark/data/stateProvinceList';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';

const page_size = 10;

const CarRegistrationViewingStage: React.FC<{
	parkingLocation: ParkingLocation;
}> = ({ parkingLocation }) => {
	const [filter, setFilter] = useState({
		search: '',
		showActive: true,
		showUpcoming: true,
		showExpired: false,
		items_per_page: page_size,
		page: 1,
	});

	const [registrations, setRegistrations] = useState<RegisteredPlates[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [deleteTarget, setDeleteTarget] = useState<RegisteredPlates | null>(
		null,
	);

	// API Fetch with Lodash Debounce
	const fetchData = useCallback(
		debounce(async (updatedFilter) => {
			setLoading(true);
			try {
				const data = await getRegisteredPlatesByParkingLocation(
					parkingLocation.id,
					updatedFilter,
				);
				setRegistrations(data);
			} catch (error) {
				console.error('❌ Failed to fetch registered plates:', error);
			} finally {
				setLoading(false);
			}
		}, 500), // 500ms debounce time
		[parkingLocation.id],
	);

	// Trigger API call when filter updates
	useEffect(() => {
		fetchData(filter);
	}, [filter, fetchData]);

	const getStatus = (validFrom: Date, validUntil: Date) => {
		const today = new Date();
		if (today < validFrom) return 'Upcoming';
		if (today > validUntil) return 'Expired';
		return 'Active';
	};

	const handleDelete = async () => {
		if (deleteTarget) {
			await deleteRegisteredPlate(parseInt(deleteTarget.id));
			setRegistrations((prev) =>
				prev.filter((reg) => reg.id !== deleteTarget.id),
			);
			setDeleteTarget(null);
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'status',
			headerName: 'Status',
			width: 150,
			renderCell: (params) => {
				const status = getStatus(
					params.row.validFrom,
					params.row.validUntil,
				);
				const color =
					status === 'Active'
						? 'success'
						: status === 'Upcoming'
						? 'warning'
						: 'error';
				return <Chip label={status} color={color} />;
			},
		},
		{ field: 'licenseNumber', headerName: 'License Plate', width: 150 },
		{
			field: 'licenseState',
			headerName: 'State',
			width: 100,
			valueGetter: (params) => {
				const stateObj = stateProvinceList.find(
					(state) => state.code === params.value,
				);
				return stateObj ? stateObj.label : params.value; // Return full name or fallback to code if not found
			},
		},
		{
			field: 'validFrom',
			headerName: 'Valid From',
			width: 250,
			valueGetter: (params) =>
				params.value
					? format(
							new Date(params.value),
							'hh:mm a, EEE, dd MMM yyyy',
					  )
					: '',
		},
		{
			field: 'validUntil',
			headerName: 'Valid Until',
			width: 250,
			valueGetter: (params) =>
				params.value
					? format(
							new Date(params.value),
							'hh:mm a, EEE, dd MMM yyyy',
					  )
					: '',
		},
		{
			field: 'duration',
			headerName: 'Duration',
			flex: 1,
			minWidth: 150,
			renderCell: (params) => {
				const duration = humanizeDuration(
					Math.abs(
						differenceInMilliseconds(
							params.row.validUntil,
							params.row.validFrom,
						),
					),
					{
						language: 'en',
						units: ['d', 'h', 'm'],
						round: true,
						delimiter: ', ',
						spacer: ' ',
					},
				);
				return <Chip label={duration} color="info" />;
			},
		},
		{
			field: 'actions',
			headerName: '',
			width: 60,
			flex: 0,
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<IconButton
					color="error"
					onClick={() => setDeleteTarget(params.row)}
				>
					<Delete />
				</IconButton>
			),
		},
	];

	return (
		<Box display={'flex'} flexDirection="column" flexGrow={1}>
			<Toolbar>
				<div style={{ flexGrow: 1 }} />
				<Box>
					<Typography variant="caption">Show Active</Typography>
					<Switch
						checked={filter.showActive}
						color="primary"
						onChange={() =>
							setFilter({
								...filter,
								showActive: !filter.showActive,
							})
						}
					/>
				</Box>
				<Box>
					<Typography variant="caption">Show Upcoming</Typography>
					<Switch
						checked={filter.showUpcoming}
						color="warning"
						onChange={() =>
							setFilter({
								...filter,
								showUpcoming: !filter.showUpcoming,
							})
						}
					/>
				</Box>
				<Box>
					<Typography variant="caption">Show Expired</Typography>
					<Switch
						checked={filter.showExpired}
						color="error"
						onChange={() =>
							setFilter({
								...filter,
								showExpired: !filter.showExpired,
							})
						}
					/>
				</Box>
				<TextField
					label="Search License Plate"
					variant="outlined"
					size="small"
					value={filter.search}
					onChange={(e) =>
						setFilter({
							...filter,
							search: normalizeLicensePlate(e.target.value),
							page: 1,
						})
					}
				/>
			</Toolbar>
			<Box flexGrow={1}>
				<DataGrid
					rows={registrations}
					columns={columns}
					pageSize={filter.items_per_page}
					pagination
					disableSelectionOnClick
					loading={loading}
					onPageChange={(newPage) =>
						setFilter({ ...filter, page: newPage + 1 })
					}
				/>
			</Box>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
			>
				<DialogTitle>Confirm Deletion</DialogTitle>
				<DialogContent>
					<Typography>
						Are you sure you want to delete registration for{' '}
						<strong>{deleteTarget?.licenseNumber}</strong>?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDeleteTarget(null)}
						color="primary"
					>
						Cancel
					</Button>
					<Button onClick={handleDelete} color="error">
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default CarRegistrationViewingStage;
